import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Tag, Loader2, Eye, EyeOff, ArrowUp, ArrowDown, Save, Trash2, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { LabelSticker, DEFAULT_LABEL_CONFIG } from "@/components/QrPrintModal";

const FIELD_LABELS = {
  qr: "QR Code", name: "Card Name", set: "Card Set",
  number: "Card Number", condition: "Card Condition", price: "Price",
};

const PRESETS = [
  { label: "Thermal roll 1 × 0.5 in · 1-up", width_in: 1.0, height_in: 0.5, columns: 1, roll: true },
  { label: "Thermal roll 2.25 × 1.25 in · 1-up", width_in: 2.25, height_in: 1.25, columns: 1, roll: true },
  { label: "Compact 1.0 × 1.3 in · 4 cols", width_in: 1.0, height_in: 1.3, columns: 4 },
  { label: "Square 1.5 × 1.5 in · 3 cols", width_in: 1.5, height_in: 1.5, columns: 3 },
  { label: "Avery 5160  2.63 × 1.0 in · 3 cols", width_in: 2.63, height_in: 1.0, columns: 3 },
  { label: "Large 2.0 × 2.5 in · 2 cols", width_in: 2.0, height_in: 2.5, columns: 2 },
];

const SAMPLE = {
  qr_token: "PKSsample00001", name: "Charizard ex", set_name: "SV: 151",
  card_number: "199/165", condition: "NM", tcg_price: 304.01,
};

const LABEL_ELEMENT_ORDER = ["qr", "barcode", "name", "set", "number", "condition", "price"];
const EL_LABELS = { qr: "QR Code", barcode: "Barcode", name: "Card Name", set: "Card Set", number: "Card Number", condition: "Condition", price: "Price" };
const EL_DEFAULTS = { qr: { w: 0.42, font: 9 }, barcode: { w: 0.6, font: 9 }, name: { w: 0.5, font: 9 }, set: { w: 0.5, font: 8 }, number: { w: 0.4, font: 8 }, condition: { w: 0.3, font: 8 }, price: { w: 0.4, font: 10 } };
const seedLayout = (cfg) => {
  let y = 0.06;
  return (cfg.fields || []).filter((f) => f.enabled).map((f) => {
    const d = EL_DEFAULTS[f.key];
    const el = { key: f.key, enabled: true, x: 0.06, y, w: d.w, font: d.font, align: "left" };
    y = Math.min(0.85, y + (f.key === "qr" ? 0.46 : 0.16));
    return el;
  });
};

export default function Labels() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setConfig({ ...DEFAULT_LABEL_CONFIG, ...(data.label_config || {}) })).catch(() => setConfig(DEFAULT_LABEL_CONFIG));
  }, []);

  if (!config) {
    return <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const setNum = (k, v, min, max) => setConfig((c) => ({ ...c, [k]: Math.max(min, Math.min(max, Number(v) || min)) }));
  const toggle = (key) => setConfig((c) => ({ ...c, fields: c.fields.map((f) => f.key === key ? { ...f, enabled: !f.enabled } : f) }));
  const move = (i, dir) => setConfig((c) => {
    const j = i + dir;
    if (j < 0 || j >= c.fields.length) return c;
    const fields = [...c.fields];
    [fields[i], fields[j]] = [fields[j], fields[i]];
    return { ...c, fields };
  });
  const applyPreset = (p) => setConfig((c) => ({ ...c, width_in: p.width_in, height_in: p.height_in, columns: p.columns, roll: !!p.roll }));
  const setRoll = (roll) => setConfig((c) => ({ ...c, roll, columns: roll ? 1 : (c.columns > 1 ? c.columns : 3) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/settings/labels", config);
      setConfig(data);
      toast.success("Label settings saved — used across all QR printing");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-100">Labels</h1>
          <p className="text-sm text-slate-400 mt-1">Choose what prints on your QR stickers and size them for your label sheets. Used everywhere you print.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="labels-save-button"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-[#1a1205] font-semibold text-sm hover:bg-amber-400 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Labels
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Fields + size */}
        <div className="lg:col-span-3 space-y-6">
          <div className="rounded-2xl border p-4 flex items-center justify-between gap-3 flex-wrap" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
            <div>
              <h2 className="font-heading font-bold text-slate-100 text-sm">Layout mode</h2>
              <p className="text-[11px] text-slate-500">Simple stacks fields; Designer lets you drag elements and add a barcode.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setConfig((c) => ({ ...c, designer: false }))} data-testid="labels-mode-simple"
                className={`px-4 py-2 rounded-xl border text-sm font-semibold ${!config.designer ? "bg-amber-500 text-[#1a1205] border-amber-500" : "text-slate-300"}`} style={config.designer ? { borderColor: "var(--pk-border)" } : {}}>Simple</button>
              <button onClick={() => setConfig((c) => ({ ...c, designer: true, layout: (c.layout && c.layout.length) ? c.layout : seedLayout(c) }))} data-testid="labels-mode-designer"
                className={`px-4 py-2 rounded-xl border text-sm font-semibold ${config.designer ? "bg-amber-500 text-[#1a1205] border-amber-500" : "text-slate-300"}`} style={!config.designer ? { borderColor: "var(--pk-border)" } : {}}>Designer</button>
            </div>
          </div>

          {config.designer ? (
            <LabelDesigner config={config} setConfig={setConfig} />
          ) : (
          <div className="rounded-2xl border p-5" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4"><Tag className="h-3.5 w-3.5" /> Fields (toggle & reorder)</div>
            <div className="space-y-2" data-testid="labels-field-list">
              {config.fields.map((f, i) => (
                <div key={f.key} data-testid={`labels-field-${f.key}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border ${f.enabled ? "" : "opacity-50"}`} style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }}>
                  <span className="w-6 text-center text-xs font-mono-num text-slate-500">{i + 1}</span>
                  <span className="flex-1 text-sm text-slate-100">{FIELD_LABELS[f.key]}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`labels-up-${f.key}`} className="h-7 w-7 rounded flex items-center justify-center text-slate-400 hover:text-amber-400 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === config.fields.length - 1} data-testid={`labels-down-${f.key}`} className="h-7 w-7 rounded flex items-center justify-center text-slate-400 hover:text-amber-400 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                    <button onClick={() => toggle(f.key)} data-testid={`labels-toggle-${f.key}`} className={`h-7 w-7 rounded flex items-center justify-center ${f.enabled ? "text-emerald-400" : "text-slate-600"}`}>
                      {f.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

          <div className="rounded-2xl border p-5" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">Printer type</div>
            <div className="grid grid-cols-2 gap-2 mb-5">
              <button onClick={() => setRoll(false)} data-testid="labels-type-sheet"
                className={`py-2.5 rounded-xl border text-sm font-semibold transition-colors ${!config.roll ? "bg-amber-500 text-[#1a1205] border-amber-500" : "text-slate-300"}`} style={config.roll ? { borderColor: "var(--pk-border)" } : {}}>Label sheet</button>
              <button onClick={() => setRoll(true)} data-testid="labels-type-roll"
                className={`py-2.5 rounded-xl border text-sm font-semibold transition-colors ${config.roll ? "bg-amber-500 text-[#1a1205] border-amber-500" : "text-slate-300"}`} style={!config.roll ? { borderColor: "var(--pk-border)" } : {}}>Thermal roll</button>
            </div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">Sticker size</div>
            <div className={`grid gap-4 ${config.roll ? "grid-cols-2" : "grid-cols-3"}`}>
              <Num label="Width (in)" value={config.width_in} onChange={(v) => setNum("width_in", v, 0.5, 4)} testid="labels-width-input" step="0.05" />
              <Num label="Height (in)" value={config.height_in} onChange={(v) => setNum("height_in", v, 0.5, 6)} testid="labels-height-input" step="0.05" />
              {!config.roll && <Num label="Columns" value={config.columns} onChange={(v) => setNum("columns", v, 1, 10)} testid="labels-columns-input" step="1" />}
            </div>
            {!config.roll && (
              <>
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <Num label="Top offset (in)" value={config.offset_top_in} onChange={(v) => setNum("offset_top_in", v, 0, 10)} testid="labels-offset-top-input" step="0.05" />
                  <Num label="Left offset (in)" value={config.offset_left_in} onChange={(v) => setNum("offset_left_in", v, 0, 10)} testid="labels-offset-left-input" step="0.05" />
                  <Num label="Gap (in)" value={config.gap_in} onChange={(v) => setNum("gap_in", v, 0, 2)} testid="labels-gap-input" step="0.05" />
                </div>
                <p className="text-[11px] text-slate-500 mt-3">Top/left offset nudges the whole sheet to skip already-used labels on a partial sheet. Gap sets the space between stickers.</p>
              </>
            )}
            {config.roll && (
              <p className="text-[11px] text-slate-500 mt-3">Thermal roll prints <strong>one label per page</strong> at exactly your width × height with no margins — ideal for 1" × 0.5" rolls. In your browser print dialog, pick the thermal printer and set paper/size to the label size (margins: None).</p>
            )}
            <div className="flex flex-wrap gap-2 mt-4">
              {PRESETS.map((p) => (
                <button key={p.label} onClick={() => applyPreset(p)} data-testid="labels-preset"
                  className="px-3 py-1.5 rounded-lg border text-xs text-slate-300 hover:bg-[var(--pk-hover)]" style={{ borderColor: "var(--pk-border)" }}>{p.label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="lg:col-span-2">
          <div className="rounded-2xl border p-5 sticky top-4" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }}>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-4">Live preview</div>
            <div className="flex justify-center rounded-xl p-4" style={{ background: "#e9edf2" }} data-testid="labels-preview">
              <LabelSticker card={SAMPLE} config={config} />
            </div>
            <p className="text-[11px] text-slate-500 mt-3 text-center">This is exactly how each sticker prints. Save to apply everywhere.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function LabelDesigner({ config, setConfig }) {
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const [sel, setSel] = useState(null);
  const layout = config.layout || [];
  const scale = Math.min(220, Math.max(120, 360 / config.width_in));
  const cw = config.width_in * scale, ch = config.height_in * scale;

  const updateEl = (key, patch) => setConfig((c) => ({ ...c, layout: (c.layout || []).map((e) => e.key === key ? { ...e, ...patch } : e) }));
  const addEl = (key) => setConfig((c) => {
    const cur = c.layout || [];
    if (cur.some((e) => e.key === key)) return { ...c, layout: cur.map((e) => e.key === key ? { ...e, enabled: true } : e) };
    const d = EL_DEFAULTS[key];
    return { ...c, layout: [...cur, { key, enabled: true, x: 0.08, y: 0.08, w: d.w, font: d.font, align: "left" }] };
  });
  const removeEl = (key) => { setConfig((c) => ({ ...c, layout: (c.layout || []).filter((e) => e.key !== key) })); if (sel === key) setSel(null); };

  const onMove = (e) => {
    const d = dragRef.current; if (!d) return;
    const x = Math.max(0, Math.min(0.98, (e.clientX - d.rect.left) / d.rect.width));
    const y = Math.max(0, Math.min(0.95, (e.clientY - d.rect.top) / d.rect.height));
    updateEl(d.key, { x, y });
  };
  const onUp = () => { dragRef.current = null; window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
  const onDown = (e, key) => {
    e.preventDefault(); e.stopPropagation(); setSel(key);
    dragRef.current = { key, rect: canvasRef.current.getBoundingClientRect() };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const present = new Set(layout.map((e) => e.key));
  const selEl = layout.find((e) => e.key === sel);
  const isImg = (k) => k === "qr" || k === "barcode";
  const val = (k) => k === "name" ? SAMPLE.name : k === "set" ? SAMPLE.set_name : k === "number" ? `#${SAMPLE.card_number}` : k === "condition" ? SAMPLE.condition : k === "price" ? `$${SAMPLE.tcg_price.toFixed(2)}` : "";

  return (
    <div className="rounded-2xl border p-5" style={{ background: "var(--pk-surface)", borderColor: "var(--pk-border)" }} data-testid="label-designer">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3"><Tag className="h-3.5 w-3.5" /> Designer — drag to arrange</div>
      <div className="flex justify-center rounded-xl p-4 mb-4" style={{ background: "#e9edf2" }}>
        <div ref={canvasRef} className="relative" style={{ width: cw, height: ch, background: "#fff", boxShadow: "0 0 0 1px #cbd5e1", overflow: "hidden" }} data-testid="designer-canvas" onPointerDown={() => setSel(null)}>
          {layout.filter((e) => e.enabled).map((el) => (
            <div key={el.key} onPointerDown={(e) => onDown(e, el.key)}
              className={`absolute cursor-move ${sel === el.key ? "ring-2 ring-amber-500" : ""}`}
              style={{ left: `${el.x * 100}%`, top: `${el.y * 100}%` }} data-testid={`designer-el-${el.key}`}>
              {el.key === "qr" ? <div style={{ width: el.w * cw, height: el.w * cw, background: "#111", color: "#fff", fontSize: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>QR</div>
                : el.key === "barcode" ? <div style={{ width: el.w * cw, height: 0.28 * ch, background: "repeating-linear-gradient(90deg,#111 0 2px,#fff 2px 4px)" }} />
                : <div style={{ fontSize: `${el.font}pt`, fontWeight: (el.key === "name" || el.key === "price" || el.key === "condition") ? 700 : 400, color: "#111", whiteSpace: "nowrap" }}>{val(el.key)}</div>}
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {LABEL_ELEMENT_ORDER.map((k) => (
          <button key={k} onClick={() => present.has(k) ? removeEl(k) : addEl(k)} data-testid={`designer-add-${k}`}
            className={`px-3 py-1.5 rounded-lg border text-xs flex items-center gap-1 ${present.has(k) ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "text-slate-300"}`} style={present.has(k) ? {} : { borderColor: "var(--pk-border)" }}>
            {present.has(k) ? <Trash2 className="h-3 w-3" /> : <Plus className="h-3 w-3" />} {EL_LABELS[k]}
          </button>
        ))}
      </div>
      {selEl ? (
        <div className="rounded-xl border p-4 space-y-3" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} data-testid="designer-props">
          <div className="text-xs font-semibold text-slate-200">{EL_LABELS[selEl.key]}</div>
          {isImg(selEl.key) ? (
            <label className="block text-xs text-slate-400">Size
              <input type="range" min="0.1" max="1" step="0.02" value={selEl.w} onChange={(e) => updateEl(selEl.key, { w: Number(e.target.value) })} className="w-full" data-testid="designer-size" />
            </label>
          ) : (
            <>
              <label className="block text-xs text-slate-400">Font size (pt): {selEl.font}
                <input type="range" min="5" max="24" step="0.5" value={selEl.font} onChange={(e) => updateEl(selEl.key, { font: Number(e.target.value) })} className="w-full" data-testid="designer-font" />
              </label>
              <div className="flex gap-2">
                {["left", "center", "right"].map((a) => (
                  <button key={a} onClick={() => updateEl(selEl.key, { align: a })} className={`px-2 py-1 rounded text-xs border capitalize ${selEl.align === a ? "bg-amber-500 text-[#1a1205] border-amber-500" : "text-slate-300"}`} style={selEl.align === a ? {} : { borderColor: "var(--pk-border)" }}>{a}</button>
                ))}
              </div>
            </>
          )}
        </div>
      ) : <p className="text-[11px] text-slate-500">Tap an element on the label to size & style it. Tap a chip above to add or remove it.</p>}
    </div>
  );
}

function Num({ label, value, onChange, testid, step }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      <input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border text-sm text-slate-100 outline-none focus:ring-2 focus:ring-amber-500/50 font-mono-num" style={{ background: "var(--pk-card)", borderColor: "var(--pk-border)" }} />
    </label>
  );
}
